/**
 * TanStack Query Key Factory
 *
 * 도메인별 typed query key 생성기. 인라인 문자열 키 사용을 대체해 drift를 방지한다.
 *
 * 사용법:
 *   useQuery({ queryKey: queryKeys.consults.list(searchParams), queryFn: ... })
 *   queryClient.invalidateQueries({ queryKey: queryKeys.consults.all })
 *
 * 키 구조 — 계층형 배열:
 *   [domain] → 도메인 전체 (invalidate 시 모든 자식 키도 무효화됨)
 *     ├ "list" → 목록 캐시 그룹
 *     │   └ filters → 특정 필터의 목록
 *     ├ "detail" → 상세 캐시 그룹
 *     │   └ id → 특정 ID 상세
 *     └ 도메인 특화 sub-key
 *
 * TanStack Query는 prefix match로 invalidate하므로 `queryKeys.consults.all` 한 번이면
 * 그 도메인의 모든 list/detail/sub 캐시가 일괄 무효화된다.
 *
 * 마이그레이션 우선순위 (B1):
 *   1. ConsultListPage
 *   2. useDashboardData
 *   3. useStatsData
 * 나머지 호출부는 prefix가 동일해 invalidate가 호환됨 (점진 마이그 가능).
 */

// 외부 타입은 의도적으로 import하지 않는다 — 이 파일이 모든 페이지에 import되므로
// 순환 의존을 방지하고 빌드 영향을 최소화. 구체적 필터 타입은 unknown으로 받고
// 사용처에서 좁혀 쓴다. as const로 리터럴 보존.

type Filters = Record<string, unknown> | undefined;

export const queryKeys = {
  // ─── Consults (상담 이력) ─────────────────────────────────────────────────
  consults: {
    all: ["consults"] as const,
    lists: () => [...queryKeys.consults.all, "list"] as const,
    list: (filters: Filters) =>
      [...queryKeys.consults.lists(), filters] as const,
    summaryCounts: (filters: Filters) =>
      [...queryKeys.consults.all, "summary-counts", filters] as const,
    details: () => [...queryKeys.consults.all, "detail"] as const,
    detail: (id: number | string | undefined) =>
      [...queryKeys.consults.details(), id] as const,
    customer: (customerId: number | undefined) =>
      [...queryKeys.consults.all, "customer", customerId] as const,
    customerPaged: (customerId: number | undefined, page: number) =>
      [...queryKeys.consults.customer(customerId), page] as const,
    callbackPendingCount: () =>
      [...queryKeys.consults.all, "callback-pending-count"] as const,
    outboundQueue: (page: number, size: number) =>
      [...queryKeys.consults.all, "outbound-queue", page, size] as const,
  },

  // ─── Customers (고객 관리) ───────────────────────────────────────────────
  customers: {
    all: ["customers"] as const,
    lists: () => [...queryKeys.customers.all, "list"] as const,
    list: (filters: Filters) =>
      [...queryKeys.customers.lists(), filters] as const,
    summaryStats: () => [...queryKeys.customers.all, "summary-stats"] as const,
    details: () => [...queryKeys.customers.all, "detail"] as const,
    detail: (id: number | undefined) =>
      [...queryKeys.customers.details(), id] as const,
    stats: (id: number | undefined) =>
      [...queryKeys.customers.all, "stats", id] as const,
    pendingConsults: (ids: number[]) =>
      [...queryKeys.customers.all, "pending-consults", ids] as const,
  },

  // ─── Appointments (예약) ─────────────────────────────────────────────────
  appointments: {
    all: ["appointments"] as const,
    lists: () => [...queryKeys.appointments.all, "list"] as const,
    list: (filters: Filters) =>
      [...queryKeys.appointments.lists(), filters] as const,
    summary: (year: number, month: number) =>
      [...queryKeys.appointments.all, "summary", year, month] as const,
    history: (id: number | undefined) =>
      [...queryKeys.appointments.all, "history", id] as const,
    detail: (uuidOrId: string | number | undefined) =>
      [...queryKeys.appointments.all, "detail", uuidOrId] as const,
    timeline: (date: string) =>
      [...queryKeys.appointments.all, "timeline", date] as const,
    customer: (customerId: number | undefined) =>
      [...queryKeys.appointments.all, "customer", customerId] as const,
    customerPaged: (customerId: number | undefined, page: number) =>
      [...queryKeys.appointments.customer(customerId), page] as const,
    customerActive: (customerId: number | undefined) =>
      [...queryKeys.appointments.all, "customer-active", customerId] as const,
    bookingStats: (customerId: number | undefined) =>
      [...queryKeys.appointments.all, "booking-stats", customerId] as const,
  },

  // ─── Dashboard (대시보드) ────────────────────────────────────────────────
  dashboard: {
    all: ["dashboard"] as const,
    stats: (mode: string, from: string, to: string) =>
      [...queryKeys.dashboard.all, "stats", mode, from, to] as const,
    todayOnly: (today: string) =>
      [...queryKeys.dashboard.all, "today-only", today] as const,
    todayComparison: (today: string) =>
      [...queryKeys.dashboard.all, "today-comparison", today] as const,
  },

  // ─── Stats (통계) ────────────────────────────────────────────────────────
  stats: {
    all: ["stats"] as const,
    summary: (from: string, to: string) =>
      [...queryKeys.stats.all, "summary", from, to] as const,
    dailyBreakdown: (from: string, to: string) =>
      [...queryKeys.stats.all, "daily-breakdown", from, to] as const,
    hourlyRange: (from: string, to: string) =>
      [...queryKeys.stats.all, "hourly-range", from, to] as const,
    department: (from: string, to: string) =>
      [...queryKeys.stats.all, "department", from, to] as const,
    callClassification: (from: string, to: string) =>
      [...queryKeys.stats.all, "call-classification", from, to] as const,
    appointmentFunnel: (from: string, to: string) =>
      [...queryKeys.stats.all, "appointment-funnel", from, to] as const,
    sentiment: (from: string, to: string) =>
      [...queryKeys.stats.all, "sentiment", from, to] as const,
    responseRateRange: (from: string, to: string) =>
      [...queryKeys.stats.all, "response-rate-range", from, to] as const,
  },

  // ─── Clinic Settings (병원 설정) ─────────────────────────────────────────
  clinicSettings: {
    all: ["clinic-settings"] as const,
    bookingRules: () => ["booking-rules"] as const,
    bookingSettings: () => ["booking-settings"] as const,
    hospitalHolidays: () => ["hospital-holidays"] as const,
    consultationMode: () => ["consultation-mode"] as const,
    aiOperatingTemplate: () => ["ai-operating-template"] as const,
    organizationPrompt: () => ["organizationPrompt"] as const,
    medicalStaff: () => ["medical-staff"] as const,
    services: () => ["services"] as const,
  },

  // ─── Organization (조직) ─────────────────────────────────────────────────
  organization: {
    info: () => ["organization"] as const,
    members: () => ["organization-members"] as const,
  },
} as const;

/**
 * Re-export type for external use (예: hook 시그니처).
 * 사용 예: `function buildKey(filters: Filters): ConsultsListKey`
 */
export type ConsultsListKey = ReturnType<typeof queryKeys.consults.list>;
export type ConsultsDetailKey = ReturnType<typeof queryKeys.consults.detail>;
export type CustomersListKey = ReturnType<typeof queryKeys.customers.list>;
export type DashboardStatsKey = ReturnType<typeof queryKeys.dashboard.stats>;
export type StatsSummaryKey = ReturnType<typeof queryKeys.stats.summary>;
